
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertTriangle, 
  CheckCircle, 
  RefreshCw, 
  Users, 
  Database,
  Shield
} from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { userAssignmentService } from '@/services/userAssignmentService';

export const DataConsistencyReport = () => {
  const { 
    data: consistencyData, 
    isLoading: isLoadingConsistency,
    refetch: refetchConsistency 
  } = useQuery({
    queryKey: ['dataConsistency'],
    queryFn: () => userAssignmentService.validateDataConsistency(),
  });

  const { 
    data: unassignedUsers, 
    isLoading: isLoadingUnassigned,
    refetch: refetchUnassigned 
  } = useQuery({
    queryKey: ['unassignedUsersCount'],
    queryFn: async () => {
      const users = await userAssignmentService.getUnassignedUsers();
      return users.length;
    },
  });

  const handleRefreshAll = () => {
    refetchConsistency();
    refetchUnassigned();
  };

  const isLoading = isLoadingConsistency || isLoadingUnassigned;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            <CardTitle>Data Consistency Report</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefreshAll}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Data Consistency Status */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Database className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Data Consistency</span>
            </div>
            
            {isLoadingConsistency ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Checking...</span>
              </div>
            ) : consistencyData ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {consistencyData.isConsistent ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        Consistent
                      </Badge>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-red-500" />
                      <Badge variant="destructive">
                        {consistencyData.issues.length} Issues
                      </Badge>
                    </>
                  )}
                </div>
                
                {!consistencyData.isConsistent && (
                  <div className="text-xs text-muted-foreground space-y-1">
                    {consistencyData.issues.slice(0, 3).map((issue, index) => (
                      <div key={index} className="truncate">• {issue.description}</div>
                    ))}
                    {consistencyData.issues.length > 3 && (
                      <div>... and {consistencyData.issues.length - 3} more</div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Failed to load
              </div>
            )}
          </div>

          {/* Unassigned Users */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Unassigned Users</span>
            </div>
            
            {isLoadingUnassigned ? (
              <div className="flex items-center gap-2">
                <RefreshCw className="h-4 w-4 animate-spin" />
                <span className="text-sm text-muted-foreground">Counting...</span>
              </div>
            ) : typeof unassignedUsers === 'number' ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  {unassignedUsers === 0 ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <Badge variant="secondary" className="bg-green-100 text-green-800">
                        All Assigned
                      </Badge>
                    </>
                  ) : (
                    <>
                      <AlertTriangle className="h-4 w-4 text-amber-500" />
                      <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                        {unassignedUsers} Users
                      </Badge>
                    </>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {unassignedUsers === 0 
                    ? 'All users have been assigned to affiliates'
                    : `${unassignedUsers} users need affiliate assignment`
                  }
                </div>
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">
                Failed to load
              </div>
            )}
          </div>

          {/* System Status */}
          <div className="border rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">System Status</span>
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                {consistencyData?.isConsistent && unassignedUsers === 0 ? (
                  <>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    <Badge variant="secondary" className="bg-green-100 text-green-800">
                      Healthy
                    </Badge>
                  </>
                ) : (
                  <>
                    <AlertTriangle className="h-4 w-4 text-amber-500" />
                    <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                      Needs Attention
                    </Badge>
                  </>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {consistencyData?.isConsistent && unassignedUsers === 0 
                  ? 'All systems operational'
                  : 'Run backfill operations to fix issues'
                }
              </div>
            </div>
          </div>
        </div>

        {/* Issues Summary */}
        {consistencyData && !consistencyData.isConsistent && (
          <div className="mt-4 p-3 border border-red-200 rounded-lg bg-red-50">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-600" />
              <span className="text-sm font-medium text-red-800">
                Data Consistency Issues Found
              </span>
            </div>
            <div className="text-xs text-red-700 space-y-1 max-h-32 overflow-y-auto">
              {consistencyData.issues.map((issue, index) => (
                <div key={index}>• {issue.description}</div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
